/**
 * A photograph goes straight to S3 (SPEC §10, invariant 7).
 *
 * The API hands out a pre-signed URL and never sees the bytes. That is what
 * keeps a child's exercise sheet off the application's disks and out of its
 * logs, and it is also what keeps a 12 MB upload from occupying an API worker
 * for thirty seconds on a domestic upstream.
 *
 * Compression happens here, before the presign: the size announced to the
 * server must be the size actually sent, or the signature will not match.
 */

import { demarrer, progresser, terminer } from "@/api/chargement";
import { call } from "@/api/client";

const MAX_EDGE = 1_600;
const QUALITY = 0.82;

export type UploadedPhoto = { key: string; width: number; height: number; bytes: number };

/** Draws the picture down to something a phone can send on a bad line. */
export async function compress(file: File): Promise<Blob & { width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("canvas indisponible");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", QUALITY),
  );
  if (!blob) throw new Error("compression impossible");
  return Object.assign(blob, { width, height });
}

/**
 * Le seul envoi de l'application dont l'avancement se **mesure**.
 *
 * Partout ailleurs le navigateur ne sait rien entre la question et la réponse,
 * et la barre de chargement avance contre une durée observée les fois d'avant
 * (`api/chargement`). Ici les octets partent un par un et se comptent, ce qui
 * vaut d'écrire la requête en `XMLHttpRequest` : `fetch` n'expose toujours pas
 * la progression d'un corps envoyé. C'est aussi l'attente la plus longue que
 * l'enfant rencontre — une photo de cahier sur l'ADSL de la maison.
 *
 * Aucun cookie ne part : la requête va au stockage d'objets, et
 * `withCredentials` reste à sa valeur par défaut, qui est `false`.
 */
function envoyerAuStockage(url: string, methode: string, corps: Blob): Promise<void> {
  const attente = demarrer("envoiDUnePhoto");
  return new Promise<void>((resoudre, rejeter) => {
    const requete = new XMLHttpRequest();
    requete.open(methode, url, true);
    requete.setRequestHeader("Content-Type", "image/jpeg");
    requete.upload.addEventListener("progress", (evenement) => {
      if (evenement.lengthComputable && evenement.total > 0) {
        progresser(attente, evenement.loaded / evenement.total);
      }
    });
    requete.addEventListener("load", () => {
      terminer(attente);
      if (requete.status >= 200 && requete.status < 300) resoudre();
      else rejeter(new Error(`envoi refusé (${requete.status})`));
    });
    requete.addEventListener("error", () => {
      terminer(attente);
      rejeter(new Error("envoi refusé (réseau)"));
    });
    requete.addEventListener("abort", () => {
      terminer(attente);
      rejeter(new Error("envoi interrompu"));
    });
    requete.send(corps);
  });
}

export async function uploadPhoto(file: File): Promise<UploadedPhoto> {
  const compressed = await compress(file);

  const presigned = await call("presign", {
    body: { content_type: "image/jpeg", content_length: compressed.size },
  });

  // `method` porte une valeur par défaut côté serveur (`Literal["PUT"]`), donc
  // le contrat la déclare facultative ; on reprend la même, plutôt que de
  // laisser la requête partir sans verbe.
  await envoyerAuStockage(presigned.url, presigned.method ?? "PUT", compressed);

  return {
    key: presigned.key,
    width: compressed.width,
    height: compressed.height,
    bytes: compressed.size,
  };
}
